import React, { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import config from 'constants/config'
import { loginApple } from 'actions/AuthActions'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalContent from 'LuxKit/Modal/ModalContent'
import SolidAppleIcon from 'LuxKit/Icons/solid/SolidAppleIcon'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import type { AccountAccessModalResult } from './AccountAccessModal.types'

const IconContainer = styled.div`
  padding: ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

interface Props {
  user: App.User
  applePrivateEmailData: App.AppleLoginData | null
  onModeChange: (nextMode: App.UiAccountModalMode) => void
  setApplePrivateEmailData: (loginData: App.AppleLoginData | null) => void
}

function AccountAccessAppleRandomEmail(props: Props) {
  const { user, onModeChange, applePrivateEmailData, setApplePrivateEmailData } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const loggedIn = useAppSelector(selectLoggedIn)
  const authProcessing = useAppSelector(state => state.auth.processing)
  const modalContext = useModalElementContext<AccountAccessModalResult>()

  const onHaveAccountClick = useCallback(() => {
    setApplePrivateEmailData(null)
    onModeChange('login')
  }, [setApplePrivateEmailData, onModeChange])

  const onCreateAccountClick = useCallback(() => {
    if (applePrivateEmailData) {
      dispatch(loginApple(applePrivateEmailData))
    }
  }, [applePrivateEmailData, dispatch])

  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  const modalTitle = intl.formatMessage({
    id: 'RQRsbTwwLaFFNqXcctpb',
    defaultMessage: 'Using Apple’s Hide My Email',
    description: 'Modal title when user signs up with Apple private email relay',
  })

  const bodyText = intl.formatMessage({
    id: 'So0Fj1rqyejtSxP2oTa0',
    defaultMessage: 'You’re about to create a {brand} account using a unique, random Apple email. If you’ve booked with a different email, those bookings won’t appear here - we recommend using the same email as your existing {brand} account.',
    description: 'Warning text explaining Apple private email relay implications for account creation',
  }, { brand: config.brandConfig.name })

  const uniqueEmailLabel = intl.formatMessage({
    id: 'YZYCI-SD2rVQgc5kaHnL',
    defaultMessage: 'Your unique email:',
    description: 'Label above the Apple-generated email address shown during sign-up',
  })

  const createAccountLabel = intl.formatMessage({
    id: '9PCQ849x8mVWUHzD0IkI',
    defaultMessage: 'Create account',
    description: 'Primary button text to create account with Apple private email',
  })

  const alreadyHaveAccountLabel = intl.formatMessage({
    id: 'XqO-04FRtG-cdeQLvHok',
    defaultMessage: 'Already have an account? Log in',
    description: 'Secondary button text to switch to login when Apple private email is detected',
  })

  return (
    <>
      <ModalHeader title={modalTitle} dismissible={false} />
      <ModalBody>
        <ModalContent>
          <Group direction="vertical" gap={20}>
            <Group direction="horizontal" gap={8}>
              <IconContainer><SolidAppleIcon /></IconContainer>
              {config.BRAND === 'luxuryescapes' && <IconContainer><LuxuryEscapesSmallLogo height={24} width={24} /></IconContainer>}
            </Group>
            <BodyText variant="large">{bodyText}</BodyText>
            <div>
              <BodyText variant="large">{uniqueEmailLabel}</BodyText>
              <BodyText variant="large" weight="bold">{user.email}</BodyText>
            </div>
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: createAccountLabel,
          onClick: onCreateAccountClick,
          disabled: authProcessing,
        }}
        secondaryActionProps={{
          children: alreadyHaveAccountLabel,
          onClick: onHaveAccountClick,
          disabled: authProcessing,
        }}
      />
    </>
  )
}

export default AccountAccessAppleRandomEmail
